import "./AdminPatients.scss";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import { useEffect, useState, useCallback } from "react";
import { GetAllPatientSuperAdmin } from "../../../utils/apiCalls/patients";
import { DeleteHealthProfessional } from "../../../utils/apiCalls/healthProfessionals";
import {
  BackArrow,
  PswdvisibleIcon,
} from "../../../assets/icons-components/Icons";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Buttons from "../../../components/Buttons/Buttons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomTooltip from "../../../components/CustomTooltip/CustomTooltip";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";
import StatusChip from "../../../components/StatusChip/StatusChip";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import useHPForm from "../HPForm/useHPForm";
import HPProfileTab from "./HPProfileTab";
import { useToast } from "../../../utils/ToastContext";
import Spinner from "../../../components/Spinner/Spinner";
import useConfirmDialog from "../../../hooks/useConfirmDialog";

const ADMIN_PATIENTS_TABLE_COLUMN_COUNT = 8;

const AdminPatients = () => {
  const { hpId } = useParams();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const rowData = location?.state?.rowData;
  const hpName = rowData?.name || sessionStorage.getItem(`hpName_${hpId}`) || "";

  useEffect(() => {
    if (rowData?.name) {
      sessionStorage.setItem(`hpName_${hpId}`, rowData.name);
    }
  }, [rowData, hpId]);

  // Scoped by hpId so returning here after viewing a session keeps this
  // HP's search/page, without leaking into a different HP's patient list.
  const searchKey = `adminPatientsSearch_${hpId}`;
  const pageKey = `adminPatientsPage_${hpId}`;
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(() => Number(sessionStorage.getItem(pageKey)) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem(searchKey) || "");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [activeTab, setActiveTab] = useState(0);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isDeletingHP, setIsDeletingHP] = useState(false);
  const deleteDialog = useConfirmDialog();

  const hpFormProps = useHPForm();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Every tab switch should land on a clean view - drop any unsaved edit
    // state instead of leaving it to leak into the next visit.
    setIsEditingProfile(false);
    hpFormProps.resetForm();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    sessionStorage.setItem(searchKey, debouncedSearchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  const fetchAllProfessionalDetail = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsLoading(true);
      setFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString,
      };
      try {
        const response = await GetAllPatientSuperAdmin(payload, hpId);
        if (response.statusCode === 200) {
          setRows(response.data);
          setTotalRows(response.totalRecords);
        } else {
          console.error(
            "Error fetching professional details:",
            response.message,
          );
          setRows([]);
          setFetchError(response.message || "Failed to load patients");
        }
      } catch (error) {
        console.error("Error fetching professional details:", error?.message);
        setRows([]);
        setFetchError(error?.message || "Failed to load patients");
      }
      setIsLoading(false);
    },
    [hpId],
  );

  useEffect(() => {
    if (activeTab === 0) {
      fetchAllProfessionalDetail(page, rowsPerPage, debouncedSearchQuery);
    }
  }, [fetchAllProfessionalDetail, debouncedSearchQuery, page, rowsPerPage, activeTab]);

  const retryFetch = () => {
    fetchAllProfessionalDetail(page, rowsPerPage, debouncedSearchQuery);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem(pageKey, String(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePatientSession = (row) => {
    navigate("/superadmin-session", { state: { rowData: row } });
    if (row?.patientId) {
      sessionStorage.setItem("rowData", row.patientId);
    } else {
      console.error("patientId is not available in the row object");
    }
  };

  const handleTable = () => {
    navigate("/hptable");
  };

  const handleDeleteHPClick = () => deleteDialog.openDialog();

  const confirmDeleteHP = async () => {
    setIsDeletingHP(true);
    try {
      const res = await DeleteHealthProfessional(hpId);
      const { statusCode, message } = res;
      if (statusCode === 200) {
        showToast(message, "success");
        navigate("/hptable");
      } else {
        showToast(message, "error");
      }
    } catch (error) {
      showToast(error?.message, "error");
    } finally {
      setIsDeletingHP(false);
      deleteDialog.closeDialog();
    }
  };

  return (
    <div className="mainp">
            
      {isDeletingHP && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}

      {/* Header showing back arrow and HP Name */}
      <div className="mainp_header">
        <div className="mainp_header_left">
          <Buttons
            icon={<BackArrow />}
            ariaLabel="Back to Healthcare Professionals"
            onClick={handleTable}
            style={{ border: "none", padding: 0, backgroundColor: "transparent" }}
          />
          <h3>{hpName || "Healthcare Professional Details"}</h3>
        </div>
        {activeTab === 0 && (
          <div className="mainp_header_right">
            <SearchInput
              className="mainp_header_right_searchbar"
              name="searchQuery"
              placeholder="Search by Patient Name, User Name or Device ID"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        )}
      </div>

      {/* Tab bar layout */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          margin: "10px 0px 20px 0px",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="healthcare professional details tabs"
        >
          <Tab label="Patients" />
          <Tab label="Profile" />
        </Tabs>
      </Box>

      {/* Tab content area */}
      <div>
        {activeTab === 0 && (
          <>
            <div className="table-container">
              <TableContainer component={Paper}>
                <Table aria-label="collapsible table">
                  <TableHead>
                    <TableRow>
                      <TableCell>SL NO</TableCell>
                      <TableCell>Patient ID</TableCell>
                      <TableCell>User Name</TableCell>
                      <TableCell>Device ID</TableCell>
                      <TableCell>Device Status</TableCell>
                      <TableCell>Last Activity Date</TableCell>
                      <TableCell>Updated Date</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows?.length === 0 ? (
                      <TableStatusRow
                        colSpan={ADMIN_PATIENTS_TABLE_COLUMN_COUNT}
                        loading={isLoading}
                        error={fetchError}
                        onRetry={retryFetch}
                        emptyMessage="No patients found"
                      />
                    ) : (
                      rows.map((row, index) => (
                        <TableRow key={row.patientId || row.deviceId}>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>{row.patientId || "-"}</TableCell>
                          <TableCell>{row.userName || "-"}</TableCell>
                          <TableCell>{row.deviceId || "-"}</TableCell>
                          <TableCell>
                            {row.deviceStatus ? (
                              <StatusChip label={row.deviceStatus} tone="neutral" />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {row.lastActivityLoggedDate &&
                              !row.lastActivityLoggedDate.includes("0001-01-01")
                              ? new Date(row.lastActivityLoggedDate).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {row.lastUploadedDate &&
                              !row.lastUploadedDate.includes("0001-01-01")
                              ? new Date(row.lastUploadedDate).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <div
                              className="table_icons"
                              style={{ display: "flex", gap: "10px" }}
                            >
                              <CustomTooltip title="View Patient Session">
                                <IconButton
                                  aria-label="patients"
                                  onClick={() => handlePatientSession(row)}
                                >
                                  <PswdvisibleIcon />
                                </IconButton>
                              </CustomTooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
            <div className="table-container">
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </>
        )}

        {activeTab === 1 && (
          <HPProfileTab
            isEditingProfile={isEditingProfile}
            setIsEditingProfile={setIsEditingProfile}
            hpFormProps={hpFormProps}
            onDeleteHP={handleDeleteHPClick}
          />
        )}
      </div>

      {deleteDialog.open && (
        <ConfirmDialog
          open={deleteDialog.open}
          onClose={deleteDialog.closeDialog}
          title="Delete Healthcare Professional"
          severity="error"
          confirmText="DELETE"
          content={
            <span>
              Are you sure you want to delete healthcare professional <strong>{hpName || "this professional"}</strong>?
            </span>
          }
          buttonText="Yes, Delete"
          additionalButtonText="Cancel"
          onButtonClick={confirmDeleteHP}
          onAdditionalButtonClick={deleteDialog.closeDialog}
        />
      )}
    </div>
  );
};

export default AdminPatients;
